import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import LoadingInner from "@/components/Loading";
import NoData from "@/components/NoData";
import FinanceeMatchLeagues from "../FinanceeMatchLeagues";
import FinanceeMatch from "../FinanceeMatch";
import { BANG_KEO_PAGE_LIMIT, BANKER, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import KbvHeadTableOdds from "../KbvHeadTableOdds";
import LeagueFilterV3 from "../LeagueFilterV3";
import BookmakerSelect from "../BookmakerSelect";
import {
    CHANGE_BOOKMAKER,
    CHANGE_BOOKMAKER_EXTEND,
    CHANGE_ODDS_PERIOD,
} from "@/lib/redux/reducers/actionTypes";
import OddsMatchLeagues from "../OrthofitOddsMatchList";
import OddsMatchRow from "../OrthofitOddsMatchList/OddsMatchRow";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV3 = ({ type = "all" }) => {
    const { colorPrimary, colorSecondary, colorBg, colorText, colorForeground, hasDataConfig } = useCompConfig();
    const [firstLoad, setFirstLoad] = useState(true);
    const [selectedDate, setSelectedDate] = useState("live");
    const [isHotOnly, setIsHotOnly] = useState(false);
    const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);

    const dispatch = useDispatch();
    const bookmarkerExtend = useSelector((state) => state.bookmarkerExtendReducer);
    const bookmarker = useSelector((state) => state.bookmakerReducer);
    const oddsPeriod = useSelector((state) => state.oddsPeriodReducer);

    const bookmakerOptions = useMemo(
        () => [
            { value: "-1", label: "Tất cả" },
            ...BANKER.map((item) => ({ value: item.value, label: item.name })),
        ],
        [],
    );

    const bookmakerExtendOptions = useMemo(
        () =>
            BANKER_EXTENDS.map((item) => ({
                value: item.value,
                label: `Tỷ lệ ${item.shortName}`,
            })),
        [],
    );

    const oddsPeriodOptions = useMemo(
        () => [
            { value: "full", label: "Cả trận" },
            { value: "half1", label: "Kèo hiệp 1" },
        ],
        [],
    );

    const today = dayjs().tz(TZ);
    const todayString = today.format("YYYY-MM-DD");

    const dateOptions = useMemo(() => {
        const options = [];

        options.push({
            value: "live",
            label: "Kèo trực tuyến",
            type: "live",
        });

        options.push({
            value: today.format("YYYY-MM-DD"),
            label: "Hôm nay",
            type: "today",
        });

        const tomorrow = today.clone().add(1, "day");
        options.push({
            value: tomorrow.format("YYYY-MM-DD"),
            label: "Ngày mai",
            type: "tomorrow",
        });

        for (let i = 2; i <= 5; i++) {
            const d = today.clone().add(i, "day");
            options.push({
                value: d.format("YYYY-MM-DD"),
                label: d.format("DD/MM"),
                type: "future",
            });
        }

        return options;
    }, [todayString]);

    const dateString =
        !selectedDate || selectedDate === ""
            ? "all"
            : selectedDate === "live"
                ? todayString
                : selectedDate;
    const status =
        type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

    const queryObject = useMemo(
        () => ({
            include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
            filterInclude: `date:${dateString}`,
            filterOdds: -1,
            cache: false,
            status,
            page: 1,
            limit: BANG_KEO_PAGE_LIMIT,
        }),
        [dateString, status],
    );

    const {
        hotLeaguesWithMatches,
        leaguesWithMatches,
        loading,
        hasMore,
        loadMoreRef,
        isLoadingMoreRef,
    } = useBangKeoMatches(queryObject);

    const baseLeaguesWithMatches = useMemo(
        () => (isHotOnly ? hotLeaguesWithMatches : leaguesWithMatches),
        [isHotOnly, hotLeaguesWithMatches, leaguesWithMatches],
    );

    const filteredLeaguesWithMatches = useMemo(() => {
        if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
        const idSet = new Set(selectedLeagueIds);
        return (baseLeaguesWithMatches || []).filter(({ league }) =>
            idSet.has(league.id),
        );
    }, [baseLeaguesWithMatches, selectedLeagueIds]);

    const matchIds = useMemo(
        () =>
            filteredLeaguesWithMatches.flatMap(
                (league) => league.matches?.map((match) => match.id) || [],
            ),
        [filteredLeaguesWithMatches],
    );

    const { clearAllData } = useLiveData();

    useLiveUpdates(matchIds);

    useEffect(() => {
        return () => {
            clearAllData();
        };
    }, [clearAllData]);

    useEffect(() => {
        setSelectedDate(todayString);
    }, [
        todayString,
    ]);

    const handleDateChange = (value) => {
        setSelectedDate(value);
        setFirstLoad(false);
    };

    const handleLeagueFilterChange = (ids) => {
        setSelectedLeagueIds(ids);
        setSelectedDate("");
        setFirstLoad(false);
    };

    const filterButtonBase =
        "inline-flex items-center justify-center rounded-lg border text-[13px] font-medium whitespace-nowrap px-[10px] py-[5px] min-h-[34px] transition-colors";

    const getFilterButtonClass = (active) =>
        `${filterButtonBase} ${active
            ? "bg-[var(--theme-primary)] text-[var(--theme-on-primary)] border-[var(--theme-primary)]"
            : "bg-[var(--theme-foreground)] text-[var(--theme-text)] border-[var(--theme-border)]"
        }`;

    const filterRowRef = useRef(null);

    const themeStyle = hasDataConfig
        ? {
              "--theme-primary": colorPrimary,
              "--theme-highlight": colorSecondary,
              "--theme-bg": colorBg,
              "--theme-text": colorText,
              "--theme-foreground": colorForeground,
          }
        : undefined;

    return (
        <div
            className="mx-auto px-2 py-3 rounded-xl flex flex-col gap-3"
            style={themeStyle}
        >
            <div className="w-full rounded-xl bg-[var(--theme-bg)] px-3 py-2">
                <div className="flex items-center gap-2 overflow-x-auto max-md:scrollbar-hide">
                    {dateOptions.map((opt) => {
                        const isSelected = selectedDate === opt.value;

                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => handleDateChange(opt.value)}
                                className={`shrink-0 ${getFilterButtonClass(isSelected)} ${opt.type === "live"
                                    ? "min-w-[110px]"
                                    : opt.type === "today" || opt.type === "tomorrow"
                                        ? "min-w-[90px]"
                                        : "min-w-[70px]"
                                    }`}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div
                ref={filterRowRef}
                className="w-full min-w-0 rounded-xl bg-[var(--theme-bg)] px-3 py-2 flex flex-wrap items-center gap-2"
            >
                <button
                    type="button"
                    onClick={() => setIsHotOnly((prev) => !prev)}
                    className={`shrink-0 min-w-[110px] ${getFilterButtonClass(isHotOnly)} py-[7px] min-h-[38px]`}
                >
                    Trận Hot
                </button>

                <LeagueFilterV3
                    anchorRef={filterRowRef}
                    selectedIds={selectedLeagueIds}
                    onChange={handleLeagueFilterChange}
                />

                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 w-full min-w-0 md:flex md:w-auto md:grid-cols-none shrink-0">
                    <BookmakerSelect
                        value={bookmarkerExtend}
                        onChange={(val) =>
                            dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
                        }
                        options={bookmakerExtendOptions}
                        placeholder="Tỷ lệ HONGKONG"
                        className="min-w-0 md:w-[160px]"
                        triggerClassName="text-[13px] px-[10px] py-[3px] min-h-[38px] rounded-lg truncate"
                        variant="outline"
                    />
                    <BookmakerSelect
                        value={bookmarker}
                        onChange={(val) =>
                            dispatch({ type: CHANGE_BOOKMAKER, payload: val })
                        }
                        options={bookmakerOptions}
                        placeholder="Tất cả"
                        className="min-w-0 md:w-[100px]"
                        triggerClassName="text-[13px] px-[10px] py-[3px] min-h-[38px] rounded-lg truncate"
                    />
                    <BookmakerSelect
                        value={oddsPeriod}
                        onChange={(val) =>
                            dispatch({ type: CHANGE_ODDS_PERIOD, payload: val })
                        }
                        options={oddsPeriodOptions}
                        placeholder="Cả trận"
                        className="hidden md:block min-w-0 md:w-[120px]"
                        triggerClassName="text-[13px] px-[10px] py-[3px] min-h-[38px] rounded-lg truncate"
                        variant="outline"
                    />
                </div>
            </div>

            {/* Danh sách trận đấu */}
            {loading && <LoadingInner />}
            {!loading && (
                <>
                    <div className="space-y-2 rounded-lg">
                        {filteredLeaguesWithMatches.length === 0 ? (
                            <NoData message="Không có trận đấu nào" />
                        ) : (
                            filteredLeaguesWithMatches.map(({ league, matches }) => (
                                <OddsMatchLeagues
                                    key={league.id}
                                    league={league}
                                    matches={matches}
                                    render={(match, matchIndex) => <OddsMatchRow key={`${match.id}-${bookmarker}`} match={match} matchIndex={matchIndex} />}
                                />
                            ))
                        )}
                    </div>
                    {hasMore && !isHotOnly && (
                        <div ref={loadMoreRef} className="py-4 text-center">
                            {isLoadingMoreRef && <LoadingInner />}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default KbvTiLeV3;